import { useEffect, useRef, useState } from 'react';

export default function HeroSection() {
  const bgRef = useRef<HTMLDivElement>(null);
  const [loaded, setLoaded] = useState(false);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 60);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const onScroll = () => {
      setScrollY(window.scrollY);
      if (bgRef.current) {
        bgRef.current.style.transform = `translateY(${window.scrollY * 0.45}px)`;
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const opacity = Math.max(0, 1 - scrollY / 500);

  return (
    <section className="relative min-h-screen bg-[#060606] overflow-hidden flex items-center">

      {/* Parallax background image */}
      <div ref={bgRef} className="absolute inset-0 will-change-transform scale-[1.15]">
        <img
          src="https://public.readdy.ai/ai/img_res/707f3148d0f519063d61b010577ff1e1.jpg"
          alt=""
          aria-hidden="true"
          className="w-full h-full object-cover object-center"
        />
      </div>

      {/* Layered overlays for premium depth */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#040404]/98 via-[#040404]/80 to-[#040404]/40" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#040404] via-transparent to-[#040404]/50" />
      <div className="absolute inset-0 bg-[#040404]/25" />

      {/* Vertical gold accent system */}
      <div className="absolute left-0 inset-y-0 w-1 bg-gradient-to-b from-transparent via-[#c9a84c]/60 to-transparent" />
      <div className="absolute left-1.5 inset-y-0 w-px bg-gradient-to-b from-transparent via-[#c9a84c]/15 to-transparent" />

      {/* Horizontal rule */}
      <div className="absolute top-28 left-0 right-0 h-px bg-gradient-to-r from-[#c9a84c]/20 via-transparent to-transparent" />

      {/* Animated light sweep */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 bottom-0 w-48 bg-gradient-to-r from-transparent via-[#c9a84c]/4 to-transparent animate-light-sweep" style={{ left: '-200px' }} />
      </div>

      {/* Main content */}
      <div
        className="relative z-10 w-full max-w-7xl mx-auto px-8 lg:px-12"
        style={{ opacity }}
      >
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-12 items-center min-h-screen py-40">

          {/* Left hero content — 7 cols */}
          <div className="xl:col-span-7">

            {/* Eyebrow label */}
            <div
              className={`flex items-center gap-4 mb-10 transition-all duration-700 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
            >
              <div className="h-px w-14 bg-[#c9a84c]" />
              <div className="h-px w-4 bg-[#c9a84c]/35" />
              <span className="text-[#c9a84c] text-[10px] font-bold tracking-[0.5em] uppercase font-sans">
                Kansas City Industrial Systems
              </span>
            </div>

            {/* Main headline */}
            <h1
              className={`font-serif text-6xl xl:text-[5.5rem] text-white leading-[1.02] mb-8 tracking-tight transition-all duration-700 delay-100 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
            >
              Precision Industrial
              <br />
              <span className="relative">
                Systems Built for{' '}
                <em className="not-italic text-[#c9a84c]">Performance</em>
                <span className="absolute -bottom-2 left-0 right-0 h-px bg-gradient-to-r from-[#c9a84c]/50 to-transparent" />
              </span>
              <br />
              <span className="text-white/70">&amp; Compliance</span>
            </h1>

            {/* Subheadline */}
            <p
              className={`text-white/50 text-xl font-sans leading-relaxed mb-14 max-w-[560px] transition-all duration-700 delay-200 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
            >
              Engineered systems, expert installation, and certified quality processes designed to meet the highest industry standards across Kansas City and surrounding regions.
            </p>

            {/* CTA row */}
            <div
              className={`flex flex-wrap items-center gap-5 mb-16 transition-all duration-700 delay-300 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
            >
              <a
                href="#contact"
                className="group relative px-10 py-5 bg-[#c9a84c] text-black font-bold text-sm tracking-widest rounded-md overflow-hidden transition-all duration-300 cursor-pointer font-sans whitespace-nowrap hover:bg-[#d4b55a]"
              >
                <span className="relative z-10 flex items-center gap-3">
                  Request a System Consultation
                  <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform" />
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-500" />
              </a>
              <a
                href="tel:8665407575"
                className="group px-10 py-5 border border-white/20 text-white font-bold text-sm tracking-wider rounded-md hover:border-[#c9a84c] hover:text-[#c9a84c] hover:bg-[#c9a84c]/5 transition-all duration-300 cursor-pointer font-sans whitespace-nowrap flex items-center gap-3"
              >
                <i className="ri-phone-line" />
                Speak With an Engineer
              </a>
            </div>

            {/* Trust micro-signals */}
            <div
              className={`flex flex-wrap items-center gap-x-8 gap-y-3 transition-all duration-700 delay-400 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
            >
              {[
                { icon: 'ri-award-line', label: '25+ Years Experience' },
                { icon: 'ri-flag-line', label: 'American-Built MAXJET' },
                { icon: 'ri-tools-line', label: 'Custom Engineered' },
                { icon: 'ri-global-line', label: 'Nationwide Delivery' },
              ].map((item) => (
                <span key={item.label} className="flex items-center gap-2 text-xs text-white/30 font-sans whitespace-nowrap">
                  <i className={`${item.icon} text-[#c9a84c]`} />
                  {item.label}
                </span>
              ))}
            </div>
          </div>

          {/* Right floating panel — 5 cols */}
          <div
            className={`xl:col-span-5 hidden xl:flex flex-col gap-5 transition-all duration-1000 delay-500 ${loaded ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-16'}`}
          >

            {/* System showcase card */}
            <div className="relative bg-black/50 backdrop-blur-md border border-[#c9a84c]/15 rounded-xl overflow-hidden">
              <div className="h-52 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=large%20industrial%20MAXJET%20parts%20washer%20machine%20in%20factory%2C%20front%20loading%20aqueous%20cleaning%20system%2C%20gleaming%20stainless%20steel%20cabinet%20washer%20in%20production%20environment%2C%20warm%20workshop%20lighting%2C%20close-up%20precision%20industrial%20equipment%2C%20American%20manufacturing&width=600&height=400&seq=hero-card-img-v3&orientation=landscape"
                  alt="MAXJET Industrial Parts Washer"
                  className="w-full h-full object-cover object-center transition-transform duration-700 hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                <div className="absolute top-4 left-4 flex items-center gap-2 px-3 py-1.5 bg-black/60 backdrop-blur-sm border border-[#c9a84c]/25 rounded-full">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#c9a84c] animate-pulse" />
                  <span className="text-[#c9a84c] text-[10px] font-bold tracking-[0.3em] uppercase font-sans">System Ready</span>
                </div>
              </div>
              <div className="p-5">
                <p className="text-white font-bold font-sans text-sm mb-1">MAXJET MJ-Series Industrial Washer</p>
                <p className="text-white/40 font-sans text-xs mb-4">Custom-engineered &bull; American built &bull; Delivered nationwide</p>
                <div className="flex flex-wrap gap-1.5">
                  {['Automotive', 'Aerospace', 'Mining', 'Military'].map((tag) => (
                    <span key={tag} className="px-2.5 py-1 bg-[#c9a84c]/8 border border-[#c9a84c]/20 rounded-full text-[#c9a84c] text-[10px] font-sans font-semibold whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Stats row */}
            <div className="grid grid-cols-3 gap-3">
              {[
                { num: '25+', label: 'Years' },
                { num: '1,000s', label: 'Systems' },
                { num: '12+', label: 'Industries' },
              ].map((s, i) => (
                <div key={i} className="px-4 py-5 bg-black/40 backdrop-blur-sm border border-white/8 rounded-xl text-center hover:border-[#c9a84c]/25 transition-colors">
                  <div className="font-serif text-3xl text-[#c9a84c] font-bold leading-none mb-1">{s.num}</div>
                  <p className="text-white/35 text-[10px] font-sans tracking-wide">{s.label}</p>
                </div>
              ))}
            </div>

            {/* Quote CTA micro-card */}
            <a
              href="https://www.kc-qualitysystems.com/custom-washer-quote/"
              className="group flex items-center justify-between px-5 py-4 bg-[#c9a84c]/8 border border-[#c9a84c]/20 rounded-xl hover:bg-[#c9a84c]/12 hover:border-[#c9a84c]/35 transition-all duration-300 cursor-pointer"
            >
              <div>
                <p className="text-white font-bold font-sans text-sm">Get a Custom Quote</p>
                <p className="text-white/40 text-xs font-sans mt-0.5">No obligation &bull; Same day response</p>
              </div>
              <div className="w-9 h-9 flex items-center justify-center rounded-full border border-[#c9a84c]/30 group-hover:border-[#c9a84c] group-hover:bg-[#c9a84c]/10 transition-all">
                <i className="ri-arrow-right-line text-[#c9a84c] text-sm" />
              </div>
            </a>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div
        className={`absolute bottom-12 left-12 flex flex-col items-start gap-3 transition-all duration-700 delay-700 ${loaded ? 'opacity-100' : 'opacity-0'}`}
      >
        <span className="text-[9px] text-white/15 font-sans tracking-[0.5em] uppercase">Scroll to Explore</span>
        <div className="w-px h-14 bg-gradient-to-b from-[#c9a84c]/40 to-transparent" />
      </div>

      <style>{`
        @keyframes light-sweep {
          0% { transform: translateX(-200px); }
          100% { transform: translateX(calc(100vw + 200px)); }
        }
        .animate-light-sweep {
          animation: light-sweep 8s ease-in-out infinite;
          animation-delay: 2s;
        }
      `}</style>
    </section>
  );
}
